import React, { useEffect, useMemo } from "react";
import { Button, Checkbox, Input, List, message, Select, Space, Switch, Typography } from "antd";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";

import { teamActions, traineesActions } from "@fitness-app/app-store";
import { PermissionKey } from "@fitness-app/data-models";
import { type TraineeStatus } from "@fitness-app/data-models/entities/Trainee";
import { getErrorMessage } from "@fitness-app/utils";

import { useTagOptions } from "~/hooks/useTagOptions";
import { type SelectedUsersListItem } from "~/modules/Chat/SendMessageToMultipleUsers/types";
import { useProductTaskAutomation } from "~/modules/Products/hooks/useProductTaskAutomation";
import { createStatusOptions } from "~/modules/Trainee/helpers/createStatusOptions";
import { useAppDispatch } from "~/store/initializeStore";

interface ComposeCampaignTargetProps {
  selectedUsers: SelectedUsersListItem[];
  setSelectedUsers: (users: SelectedUsersListItem[]) => void;
  setSelectedTags: (tags: string[]) => void;
  selectedTags: string[];
  condition: "hasTag" | "notHasTag" | false;
  changeCondition: (value: "hasTag" | "notHasTag" | false) => void;
  filterByProducts: boolean;
  toggleFilterByProduct: (value: boolean) => void;
  setFilterByProducts: (productIds: string[]) => void;
  filteredProductIds: string[];
  setStatuses: (statuses: TraineeStatus[]) => void;
  statuses: TraineeStatus[];
}

const { Search } = Input;

export const ComposeCampaignTarget = ({
  selectedUsers,
  setSelectedUsers,
  setSelectedTags,
  changeCondition,
  condition,
  filterByProducts,
  toggleFilterByProduct,
  setFilterByProducts,
  filteredProductIds,
  selectedTags,
  statuses,
  setStatuses,
}: ComposeCampaignTargetProps): React.ReactElement => {
  const [checkAll, setCheckAll] = React.useState(false);
  const [indeterminate, setIndeterminate] = React.useState(false);
  const { options: tags } = useTagOptions("traineeTags");
  const dispatch = useAppDispatch();
  const { t } = useTranslation("dashboard");
  const [clientList, setClientList] = React.useState<SelectedUsersListItem[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const { productWithPrices } = useProductTaskAutomation();
  const clientsById = useMemo(() => keyBy(clientList, "id"), [clientList]);

  useEffect(() => {
    void dispatch(teamActions.fetchTrainerConfig());
    // Scoped by `campaigns.manage`, the key the delivery use case authorizes on — see
    // ProcessScheduledPopOutMessageUseCase, which resolves the recipient scope from it (TRE-2148).
    dispatch(traineesActions.fetchAllActiveTrainees({ scopeKey: PermissionKey.CAMPAIGNS_MANAGE }))
      .unwrap()
      .then((response) => {
        setClientList(response.list as SelectedUsersListItem[]);
      })
      .catch((e) => message.error(getErrorMessage(e)));
  }, [dispatch]);

  const shouldHideList = Boolean(condition) || filterByProducts;

  const selectedById = useMemo(() => keyBy(selectedUsers, "id"), [selectedUsers]);

  const onItemChange = (checked: boolean, id: string) => {
    if (checked && !selectedById[id]) {
      const selected = clientsById[id];
      setSelectedUsers(selected ? [...selectedUsers, selected] : selectedUsers);
    } else if (!checked && selectedById[id]) {
      setSelectedUsers(selectedUsers.filter((item) => item.id !== id));
    }
  };

  const filteredClients = useMemo(() => {
    if (searchTerm.length > 0) {
      return clientList.filter((client) => {
        return (
          client.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.lastName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    return clientList;
  }, [clientList, searchTerm]);

  const statusesList = useMemo(() => {
    const list = createStatusOptions();

    return list;
  }, []);

  return (
    <div>
      <Space direction="vertical" size={16}>
        <Space>
          <Switch
            checked={filterByProducts}
            onChange={(checked) => {
              toggleFilterByProduct(checked);
              if (!checked) {
                setFilterByProducts([]);
              }
            }}
          />
          <Typography.Text>Wyślij do wszystkich klientów którzy posiadają produkt:</Typography.Text>
        </Space>
        {filterByProducts && (
          <Select
            placeholder="Wybierz produkty..."
            style={{ minWidth: 250 }}
            options={productWithPrices.map((product) => ({
              value: product.id,
              label: product.name,
            }))}
            mode="multiple"
            maxTagCount={10}
            value={filteredProductIds}
            onChange={setFilterByProducts}
          />
        )}
        <Space>
          <Switch
            checked={condition === "hasTag"}
            onChange={(active) => {
              changeCondition(active ? "hasTag" : false);
              setSelectedTags([]);
            }}
          />
          <Typography.Text>Wyślij do wszystkich klientów którzy posiadają tag:</Typography.Text>
        </Space>
        {condition === "hasTag" && (
          <Select
            placeholder="Wybierz tagi..."
            style={{ minWidth: 250 }}
            options={tags}
            mode="multiple"
            maxTagCount={10}
            value={selectedTags}
            onChange={setSelectedTags}
          />
        )}
        <Space>
          <Switch
            checked={condition === "notHasTag"}
            onChange={(active) => {
              changeCondition(active ? "notHasTag" : false);
              setSelectedTags([]);
            }}
          />
          <Typography.Text>Wyślij do wszystkich klientów którzy nie posiadają tag:</Typography.Text>
        </Space>

        {condition === "notHasTag" && (
          <Select
            placeholder="Wybierz tagi..."
            style={{ minWidth: 250 }}
            mode="multiple"
            maxTagCount={10}
            options={tags}
            value={selectedTags}
            onChange={setSelectedTags}
          />
        )}

        {shouldHideList ? (
          <Space direction="vertical">
            <Typography.Text>Wyślij do wszystkich klientów którzy ze statusem:</Typography.Text>
            <Select mode="multiple" style={{ minWidth: 250 }} value={statuses} onChange={setStatuses}>
              {statusesList.map((status) => (
                <Select.Option key={status.value} value={status.value} label={status.value}>
                  {status.text}
                </Select.Option>
              ))}
            </Select>
          </Space>
        ) : null}

        {shouldHideList ? null : (
          <Checkbox
            indeterminate={indeterminate}
            onChange={(value) => {
              setCheckAll(value.target.checked);
              setIndeterminate(false);
              // Operates only on what the search shows — checking must not select the whole
              // roster, and unchecking must not drop clients picked under a previous search.
              const visibleIds = new Set(filteredClients.map((client) => client.id));
              setSelectedUsers(
                value.target.checked
                  ? [...selectedUsers.filter((client) => !visibleIds.has(client.id)), ...filteredClients]
                  : selectedUsers.filter((client) => !visibleIds.has(client.id)),
              );
            }}
            checked={checkAll}
          >
            Zaznacz {searchTerm ? "przefiltrowanych" : "wszystkich"} klientów ({selectedUsers.length})
          </Checkbox>
        )}
      </Space>

      {!shouldHideList && (
        <div className="mt-4 flex flex-col gap-y-2">
          <Space size={4}>
            <Search
              value={searchTerm}
              placeholder="Wyszkuj klienta..."
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
            />
            {Boolean(searchTerm) && (
              <Button onClick={() => setSearchTerm("")} type="link">
                {t("products:search.reset")}
              </Button>
            )}
          </Space>
          <Checkbox.Group value={selectedUsers.map((item) => item.id)}>
            <List
              itemLayout="horizontal"
              split={false}
              pagination={{
                defaultCurrent: 1,
                defaultPageSize: 20,
                hideOnSinglePage: true,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100],
                showTotal: (total) => `${total} klientów`,
              }}
              dataSource={filteredClients}
              renderItem={(item) => (
                <List.Item>
                  <Checkbox value={item.id} onChange={(event) => onItemChange(event.target.checked, item.id)}>
                    {item.firstName} {item.lastName}
                  </Checkbox>
                </List.Item>
              )}
              locale={{ emptyText: "Brak aktywnych klientów" }}
            />
          </Checkbox.Group>
        </div>
      )}
    </div>
  );
};
